import React, { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Plus,
  Eye,
  Pencil,
  Copy,
  Trash2,
  CalendarDays,
  Search,
  Stamp,
} from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import SportCategoryChip from "components/SportCategoryChip";
import { useAuth } from "contexts/AuthContext";
import { useDialog } from "contexts/DialogContext";
import { useI18n } from "i18n";
import api from "lib/api";
import {
  EVENT_STATUSES,
  categoryLabel,
  formatEventDates,
  formatEventPlace,
} from "lib/events";

const STATUS_STYLE = {
  draft: "bg-muted text-muted-foreground",
  published: "bg-emerald-500/10 text-emerald-500",
  // Acessivel por link, fora das listagens — nem verde (publico) nem cinza.
  unlisted: "bg-sky-500/10 text-sky-500",
  archived: "bg-amber-500/10 text-amber-500",
};

// Coluna "Recursos": um icone por configuracao ligada no evento. Para um
// indicador novo, basta uma entrada aqui (campo do evento + icone + chave i18n).
const EVENT_FEATURES = [
  { field: "watermark", icon: Stamp, labelKey: "event.feature.watermark" },
];

export default function Events() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { confirm, alert } = useDialog();
  const { t, tf, lang } = useI18n();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const canManage = !!user?.capabilities?.manage_events;

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/events/", {
        params: { search: search || undefined, status: status || undefined },
      });
      setEvents(res.data);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    if (!canManage) return undefined;
    const timer = setTimeout(fetchEvents, 300);
    return () => clearTimeout(timer);
  }, [fetchEvents, canManage]);

  // Menor privilegio: quem nao administra eventos nao chega nesta tela.
  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleDelete = async (event) => {
    let failed = false;
    const confirmed = await confirm({
      title: t("event.deleteTitle"),
      description: tf("event.deleteBody", { name: event.name }),
      variant: "danger",
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          await api.delete(`/api/events/${event.id}/`);
        } catch (err) {
          failed = true;
          throw err;
        }
      },
    });

    if (confirmed) {
      fetchEvents();
      return;
    }

    if (failed) {
      await alert({
        title: t("common.deleteError"),
        description: t("error.generic"),
        variant: "danger",
      });
    }
  };

  return (
    <div className="w-full animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("event.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("event.subtitle")}</p>
        </div>
        <Button onClick={() => navigate("/app/events/new")}>
          <Plus className="h-4 w-4 mr-2" />
          {t("event.new")}
        </Button>
      </div>

      <div className="grid gap-3 mb-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("event.searchPlaceholder")}
            className="pl-9"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t("event.status")}
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">{t("event.allStatuses")}</option>
          {EVENT_STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`event.status.${value}`)}
            </option>
          ))}
        </select>
      </div>

      <Card className="w-full">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <CalendarDays className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("event.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.name")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.sportCategory")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.dates")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.place")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.photos")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.status")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.features")}
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr
                      key={event.id}
                      onClick={() => navigate(`/app/events/${event.id}`)}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{event.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {event.category === "sports" && event.sport_name
                            ? `${categoryLabel(t, event.category)} · ${event.sport_name}`
                            : categoryLabel(t, event.category)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {event.sport_category_chip ? (
                          <SportCategoryChip
                            chip={event.sport_category_chip}
                            className="whitespace-nowrap px-2 py-0.5"
                          />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatEventDates(event, lang)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatEventPlace(event) || "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {event.photo_count}
                        {event.pending_count > 0 && (
                          <span className="ml-2 text-xs text-amber-500">
                            {tf("event.pendingCount", { count: event.pending_count })}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLE[event.status] || STATUS_STYLE.draft
                          }`}
                        >
                          {t(`event.status.${event.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <EventFeatures event={event} t={t} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div
                          className="flex items-center justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => navigate(`/app/events/${event.id}`)}
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title={t("common.details")}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/app/events/${event.id}/edit`)}
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title={t("common.edit")}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {/* Duplicar abre o formulario novo ja preenchido —
                              nada e criado ate o usuario salvar. */}
                          <button
                            onClick={() =>
                              navigate("/app/events/new", {
                                state: {
                                  duplicateFrom: event.id,
                                  duplicateName: event.name,
                                },
                              })
                            }
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title={t("event.duplicate")}
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(event)}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-muted-foreground hover:text-red-600"
                            title={t("common.delete")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EventFeatures({ event, t }) {
  const active = EVENT_FEATURES.filter((item) => event[item.field]);
  if (!active.length) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex items-center gap-2">
      {active.map(({ field, icon: Icon, labelKey }) => (
        <span
          key={field}
          title={t(labelKey)}
          aria-label={t(labelKey)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"
        >
          <Icon className="h-4 w-4" />
        </span>
      ))}
    </div>
  );
}
